import { AgGridReact } from '@ag-grid-community/react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { useAgGrid } from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import { Export as ExportIcon } from '@tassat/icon';
import React from 'react';

import history from 'Lib/browserHistory';
import { exportAdminUsers } from 'Lib/exports/userExports';
import 'SharedComponents/agGrid/styles/agGridOverrides.css';
import UserStatusCellRenderer from 'SharedComponents/cellRenderComponent/userStatusCellRenderer';

import { adminUsersColumnDefs } from './adminUsersGridOptions';

import type { AdminUsersGridProps } from '.';

export function AdminUsersGrid(props: AdminUsersGridProps) {
    const {
        classes,
        filterModel,
        sortModel,
        setFilterModel,
        setSortModel,
        location,
        adminUsers
    } = props;

    const adminUserId = (props.match && props.match.params.userId) || props.adminUserId;

    const initializeGrid = (params) => {
        if (adminUserId && params.api.getRenderedNodes().length) {
            const selectedUserIndex = params.api.getRenderedNodes()
                .findIndex(rowData => rowData.data.UserId === adminUserId);
            params.api.ensureIndexVisible(selectedUserIndex, 'top');
        }

        if (location?.state?.filterModel) {
            params.api.setFilterModel(location.state.filterModel);
        }

        if (location?.state?.sortModel) {
            params.api.applyColumnState({
                state: location.state.sortModel,
                applyOrder: true
            });
        } else {
            params.api.applyColumnState({
                state: [{ colId: '0', sort: 'asc' }],
                applyOrder: true
            });
        }
    };

    const [ , onGridReady ] = useAgGrid(initializeGrid);
    const [ exportData, setExportData ] = React.useState<boolean>(false);

    const saveFilterModel = (params) => {
        setFilterModel(params.api.getFilterModel());
    };

    const saveSortModel = (params) => {
        const columnState = params.api.getColumnState();
        setSortModel(columnState.map(column => (
            { colId: column.colId, sort: column.sort }
        )));
    };

    const userSelected = (e) => {
        const { data } = e;

        history.push(`/admin/users/${data.UserId}`, {
            filterModel,
            sortModel
        });
    };

    React.useEffect(() => {
        if (exportData) {
            setExportData(false);
            exportAdminUsers();
        }
    }, [exportData]);

    return (
        <div
            className={`ag-theme-material ${classes.root}`}
            id='adminUsersGrid'
        >
            <ToolbarButton
                className={classes.exportButton}
                Icon={ExportIcon}
                iconClassName={classes.exportIcon}
                iconColor='rgba(0, 0, 0, 0.66)'
                id='adminUsersGridExportButton'
                onClick={() => setExportData(true)}
                text='Export'
            />
            <AgGridReact
                animateRows={false}
                columnDefs={adminUsersColumnDefs}
                columnMenu='legacy'
                components={{ userStatusCellRenderer: UserStatusCellRenderer }}
                defaultColDef={{
                    resizable: false,
                    filter: true,
                    sortable: true
                }}
                enableCellTextSelection={true}
                getRowId={({ data }) => data.UserId}
                onFilterChanged={saveFilterModel}
                onGridReady={onGridReady}
                onRowClicked={userSelected}
                onSortChanged={saveSortModel}
                rowClassRules={{ 'lockedRow': ({ data }) => data.IsLocked }}
                rowData={adminUsers}
                suppressCellFocus={true}
                suppressContextMenu={true}
            />
        </div>
    );
}